// تحديد الموقع الجغرافي.
//
// الموقع كان محلياً داخل شاشة المواقيت، فبعد تحديده بالـ GPS تعرض تلك
// الشاشة مدينتك بينما يبقى خط اليوم في الرئيسية على مكة — شاشتان تقولان
// وقتين مختلفين لنفس اللحظة. الآن مصدر واحد يقرأ منه الاثنان.

const GPS_KEY = 'moslam_gps_location';
// v2: قبل الملحق الأصلي كان «سُئل» يُحفظ دون أن يُسأل أحد فعلاً داخل
// التطبيق، فتغيير المفتاح يمنح كل جهاز قائم سؤالاً واحداً يعمل
const ASKED_KEY = 'moslam_gps_asked_v2';

// الموقع لا يتغيّر بسرعة، لكن المسافر يحتاج تحديثاً. نصف يوم توازن
// معقول بين الدقة وعدم إزعاج الجهاز بطلبات متكررة.
const STALE_MS = 12 * 60 * 60 * 1000;

export function readStoredLocation() {
  try {
    const raw = localStorage.getItem(GPS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.lat === 'number' && typeof parsed?.lng === 'number' ? parsed : null;
  } catch {
    return null;
  }
}

export function storeLocation(loc) {
  try {
    localStorage.setItem(GPS_KEY, JSON.stringify({ ...loc, at: Date.now() }));
  } catch {
    /* التخزين محظور — الموقع يبقى في الذاكرة */
  }
}

export function clearStoredLocation() {
  try {
    localStorage.removeItem(GPS_KEY);
  } catch {
    /* تجاهل */
  }
}

export const isStale = (loc) => !loc?.at || Date.now() - loc.at > STALE_MS;

/*
  داخل التطبيق: ملحق الموقع الأصلي، لا واجهة المتصفح.

  navigator.permissions داخل WebView لا تعكس إذن أندرويد، فكانت تقول
  «مرفوض» لمن لم يُسأل قط، فيتوقف التحديد التلقائي بصمت ويبقى التطبيق
  على مكة — والأذان يرتفع بتوقيتها في الرياض. الملحق يسأل النظام نفسه،
  ويطلب الإذن بنافذته.
*/
const nativeGeo = () =>
  typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()
    ? window.Capacitor.Plugins?.Geolocation ?? null
    : null;

// الملحق يعيد prompt-with-rationale لمن رفض مرة ويجوز سؤاله ثانية
const normalizeState = (s) => (s === 'prompt-with-rationale' ? 'prompt' : s);

/**
 * حالة إذن الموقع دون إظهار أي نافذة.
 * @returns {Promise<'granted'|'prompt'|'denied'|'unsupported'>}
 */
export async function permissionState() {
  const geo = nativeGeo();
  if (geo) {
    try {
      const { location, coarseLocation } = await geo.checkPermissions();
      // التقريبي يكفي للمواقيت: من منحه وحده منح ما نحتاج
      return location === 'granted' || coarseLocation === 'granted'
        ? 'granted'
        : normalizeState(location);
    } catch {
      return 'unsupported';
    }
  }

  if (typeof navigator === 'undefined' || !navigator.geolocation) return 'unsupported';
  if (!navigator.permissions?.query) return 'prompt'; // لا سبيل للمعرفة دون سؤال
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state;
  } catch {
    return 'prompt';
  }
}

/** اسم المدينة من الإحداثيات. فشلُه لا يمنع حساب المواقيت. */
async function reverseGeocode(lat, lng) {
  const fallback = {
    name: `موقعي (${lat.toFixed(2)}°, ${lng.toFixed(2)}°)`,
    country: 'تحديد تلقائي',
  };

  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=ar&zoom=10`,
      { signal: controller.signal, headers: { Accept: 'application/json' } }
    );
    clearTimeout(timer);
    if (!res.ok) return fallback;

    const data = await res.json();
    const a = data?.address ?? {};
    return {
      name: a.city || a.town || a.village || a.state || fallback.name,
      country: a.country || fallback.country,
    };
  } catch {
    return fallback;
  }
}

/**
 * طريقة حساب مناسبة للمنطقة الجغرافية.
 *
 * موقع الـ GPS لا يحمل طريقة، فكان يسقط على "أم القرى" أينما كان
 * المستخدم — وهي طريقة الحرمين، غير مناسبة لمن في أمريكا أو أوروبا
 * حيث تختلف زوايا الفجر والعشاء اختلافاً معتبراً.
 */
export function inferMethod(lat, lng) {
  // شبه الجزيرة العربية
  if (lat > 12 && lat < 33 && lng > 34 && lng < 60) return 'UmmAlQura';
  // مصر والسودان وشمال أفريقيا الشرقي
  if (lat > 8 && lat < 33 && lng > 24 && lng < 37) return 'Egyptian';
  // تركيا
  if (lat > 35 && lat < 43 && lng > 25 && lng < 45) return 'Turkey';
  // شبه القارة الهندية
  if (lat > 5 && lat < 38 && lng > 60 && lng < 92) return 'Karachi';
  // الأمريكتان
  if (lng < -30) return 'NorthAmerica';
  // ما بقي: رابطة العالم الإسلامي، الأوسع استخداماً عالمياً
  return 'MuslimWorldLeague';
}

const DENIED = 1; // رمز الرفض في GeolocationPositionError

const getPosition = async (options) => {
  const geo = nativeGeo();
  if (geo) {
    // getCurrentPosition في الملحق لا يطلب الإذن بنفسه على كل الإصدارات
    if ((await permissionState()) !== 'granted') {
      const res = await geo.requestPermissions({ permissions: ['location', 'coarseLocation'] });
      if (res.location !== 'granted' && res.coarseLocation !== 'granted') {
        throw { code: DENIED };
      }
    }
    return geo.getCurrentPosition(options);
  }

  return new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, options)
  );
};

/**
 * يحدّد الموقع ويعيد كائناً جاهزاً للاستخدام في حساب المواقيت.
 * @param {{ highAccuracy?: boolean }} opts
 */
export async function detectLocation({ highAccuracy = true } = {}) {
  if (!nativeGeo() && !navigator.geolocation) {
    throw Object.assign(new Error('خاصية تحديد الموقع غير مدعومة على هذا الجهاز.'), {
      code: 'UNSUPPORTED',
    });
  }

  let position;
  try {
    position = await getPosition({
      timeout: 10000,
      enableHighAccuracy: highAccuracy,
      maximumAge: 5 * 60 * 1000,
    });
  } catch (err) {
    const denied = err?.code === DENIED;
    throw Object.assign(
      new Error(
        denied
          ? 'رُفض إذن الموقع. يمكنك اختيار مدينتك يدوياً من القائمة.'
          : 'تعذر تحديد الموقع. حاول مجدداً أو اختر مدينتك يدوياً.'
      ),
      { code: denied ? 'DENIED' : 'UNAVAILABLE' }
    );
  }

  const { latitude: lat, longitude: lng } = position.coords;
  const { name, country } = await reverseGeocode(lat, lng);

  const located = {
    lat,
    lng,
    name,
    country,
    method: inferMethod(lat, lng),
    // موقع GPS هو موقع الجهاز فعلاً، فمنطقته الزمنية هي منطقة الجهاز
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  storeLocation(located);
  return located;
}

/**
 * تحديد صامت عند الإقلاع.
 *
 * نطلب الإذن مرة واحدة فقط: تكرار النافذة على من رفضها مزعج، ونظام
 * التشغيل يتجاهلها بعد رفضين على أي حال. من رفض يستخدم اختيار المدينة
 * اليدوي، ويبقى زر "موقعي" متاحاً متى غيّر رأيه.
 *
 * @returns {Promise<object|null>} الموقع، أو null إن تعذّر دون إزعاج المستخدم
 */
export async function autoDetect() {
  const state = await permissionState();
  if (state === 'unsupported' || state === 'denied') return null;

  const stored = readStoredLocation();

  // الإذن ممنوح: نحدّث بصمت إن كان المخزّن قديماً، ولا ننتظر إن كان حديثاً
  if (state === 'granted') {
    if (stored && !isStale(stored)) return stored;
    try {
      return await detectQuietly();
    } catch {
      return stored;
    }
  }

  // الإذن لم يُطلب بعد: نطلبه مرة واحدة
  let asked = false;
  try {
    asked = localStorage.getItem(ASKED_KEY) === '1';
  } catch {
    /* تجاهل */
  }
  if (asked) return stored;

  try {
    localStorage.setItem(ASKED_KEY, '1');
  } catch {
    /* تجاهل */
  }

  try {
    return await detectQuietly();
  } catch {
    return stored;
  }
}

/**
 * الدقة المنخفضة أولاً، ثم العالية إن فشلت.
 *
 * المنخفضة من الشبكة والواي فاي: أسرع وأرفق بالبطارية، وتكفي للمواقيت.
 * لكن بعض الأجهزة لا تملكها (مسح الواي فاي مطفأ، أو بلا خدمات جوجل
 * للموقع)، فكانت تفشل ويبقى التطبيق على مكة صامتاً والـ GPS متاح.
 * والرفض لا يُعاد: من رفض الإذن لا يُسأل ثانية بدقة أخرى.
 */
async function detectQuietly() {
  try {
    return await detectLocation({ highAccuracy: false });
  } catch (err) {
    if (err?.code === 'DENIED') throw err;
    return detectLocation({ highAccuracy: true });
  }
}
